import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import { ONE_MINUTE_IN_MILLIS } from "@mirage/shared/util/constants";
import { register, unregister } from "@mirage/shared/util/jobs";
import { shouldSyncRecommendations } from "./utils/should-sync-recommendations";

import { getPeopleSuggestions } from "@mirage/service-people";
import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import {
  deduplicateAndMergeExtractedPeople,
  extractValidPersonFromRecommendation,
  removeDeactivatedAccounts,
  toPeopleRecommendation,
} from "@mirage/shared/search/people-recommendations";
import { Recommendation } from "@mirage/shared/search/recommendation";
import { isDefined } from "@mirage/shared/util/tiny-utils";

const logger = tagged("typeahead/sync-people-recommendations");

const SYNC_RECOMMENDATIONS_JOB_NAME = "people-recommendations-sync";
export const SYNC_INTERVAL_MS = ONE_MINUTE_IN_MILLIS * 20;

export function start(cache: TypeaheadCache) {
  register(SYNC_RECOMMENDATIONS_JOB_NAME, SYNC_INTERVAL_MS, true, () =>
    sync(cache),
  );
}

export function cancel() {
  unregister(SYNC_RECOMMENDATIONS_JOB_NAME);
}

// sync, clear, and insert logic
const metrics = namespace("typeahead");
export async function sync(cache: TypeaheadCache) {
  try {
    const lastPeopleRecommendationsSyncMs =
      await cache.getLastPeopleRecommendationsSyncMs();

    if (
      shouldSyncRecommendations(
        lastPeopleRecommendationsSyncMs,
        "people-recommendations",
        SYNC_INTERVAL_MS,
      )
    ) {
      const personObjects = await getPeopleSuggestions({
        limit: 1000,
      });

      metrics.stats("sync/count", personObjects?.length || 0);
      metrics.counter("sync/status", 1, {
        status: "success",
      });

      const omniboxRecommendations = cache.all(
        CacheKey.OmniboxRecommendations,
      ) as Recommendation<"omnibox">[];

      const extractedPersonObjects = omniboxRecommendations
        .map(extractValidPersonFromRecommendation)
        .filter(isDefined);

      const mergedPeople = deduplicateAndMergeExtractedPeople(
        extractedPersonObjects,
        personObjects,
      );

      const peopleRecommendations = mergedPeople.map(toPeopleRecommendation);

      const activePeopleRecommendations = peopleRecommendations.filter(
        removeDeactivatedAccounts,
      );

      await cache.clear(CacheKey.PeopleRecommendations);
      await cache.cachePeopleRecommendations(activePeopleRecommendations);
      await cache.savePeopleRecommendationsSyncedMs();
    }
  } catch (e) {
    logger.error("sync failed", e);
    metrics.counter("sync/status", 1, {
      status: "error",
    });
    throw e;
  }
}
